import { getBookingMeetUrl } from "~/env";
import type { OpenIdToken } from "~/lib/matrix";

export type InstantMeetingErrorKind =
  | "unauthorized"
  | "forbidden"
  | "gone"
  | "not_found"
  | "failed";

export class InstantMeetingError extends Error {
  constructor(public readonly kind: InstantMeetingErrorKind) {
    super(`instant meeting request failed: ${kind}`);
    this.name = "InstantMeetingError";
  }
}

export interface InstantMeetingResult {
  meetingId: string;
  roomId: string;
  slug: string;
  title: string;
  startedAt: string;
  baseJoinUrl: string;
  /** Member-only join capability. `null` when booking returns an
   *  already-active meeting (idempotent re-entry) — the original
   *  capability is only minted once. */
  joinCapability: string | null;
  recapBotUserId: string | null;
}

export type TokenProvider = () => Promise<string>;

export function cachedOpenIdProvider(
  fetchToken: () => Promise<OpenIdToken>,
  now: () => number = Date.now,
): TokenProvider {
  let cached: { token: string; expiresAt: number } | null = null;
  return async () => {
    if (cached && cached.expiresAt - 60_000 > now()) return cached.token;
    const next = await fetchToken();
    cached = { token: next.accessToken, expiresAt: now() + next.expiresIn * 1000 };
    return cached.token;
  };
}

function errorKind(status: number): InstantMeetingErrorKind {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 410) return "gone";
  if (status === 404) return "not_found";
  return "failed";
}

async function postJson(
  baseUrl: string,
  path: string,
  body: Record<string, unknown>,
  getToken: TokenProvider,
): Promise<unknown> {
  let token: string;
  try {
    token = await getToken();
  } catch {
    throw new InstantMeetingError("unauthorized");
  }
  let res: Response;
  try {
    res = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch {
    throw new InstantMeetingError("failed");
  }
  if (!res.ok) throw new InstantMeetingError(errorKind(res.status));
  try {
    return await res.json();
  } catch {
    throw new InstantMeetingError("failed");
  }
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export async function createInstantMeeting(
  matrixRoomId: string,
  getToken: TokenProvider,
  title?: string,
  orgSpaceId?: string,
  baseUrl: string = getBookingMeetUrl(),
): Promise<InstantMeetingResult> {
  const json = await postJson(
    baseUrl,
    "/api/v1/meet-instant",
    {
      matrixRoomId,
      ...(title ? { title } : {}),
      ...(orgSpaceId ? { orgSpaceId } : {}),
    },
    getToken,
  );
  const obj = (json && typeof json === "object" ? json : {}) as Record<string, unknown>;
  return {
    meetingId: asString(obj.meetingId),
    roomId: asString(obj.roomId),
    slug: asString(obj.slug),
    title: asString(obj.title),
    startedAt: asString(obj.startedAt),
    baseJoinUrl: asString(obj.baseJoinUrl),
    joinCapability: typeof obj.joinCapability === "string" ? obj.joinCapability : null,
    recapBotUserId: asString(obj.recapBotUserId) || null,
  };
}

export async function mintJoinToken(
  meetingId: string,
  joinCapability: string,
  getToken: TokenProvider,
  baseUrl: string = getBookingMeetUrl(),
): Promise<string> {
  const json = await postJson(
    baseUrl,
    `/api/v1/meet-instant/${encodeURIComponent(meetingId)}/join-token`,
    {
      joinCapability,
    },
    getToken,
  );
  const obj = (json && typeof json === "object" ? json : {}) as Record<string, unknown>;
  const joinUrl = asString(obj.joinUrl);
  if (!joinUrl) throw new InstantMeetingError("failed");
  return joinUrl;
}

export async function endInstantMeeting(
  meetingId: string,
  getToken: TokenProvider,
  baseUrl: string = getBookingMeetUrl(),
): Promise<void> {
  await postJson(
    baseUrl,
    `/api/v1/meet-instant/${encodeURIComponent(meetingId)}/end`,
    {},
    getToken,
  );
}
